import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AuthShell } from '@/components/auth/AuthShell';
import { ExternalLink } from '@/components/ui/ExternalLink';
import { FieldLabel, MonoLabel } from '@/components/editorial';
import { CopySpan } from '@/lib/copyRender';
import { useCopy } from '@/hooks/useCopy';
import type { ProseSlotKey, SpanSlotKey } from '@/lib/copyResolve';
import { api, stashApplyCode } from '@/services/api';
import { ReferralBlock, type ReferralValue } from './ReferralBlock';

/**
 * B6 (F-013) — referral stop screen for /apply.
 *
 * Two original variants (B6b):
 * - 'missing' (default): application_codes_mode (ConfigSettings, #1018,
 *   fetched via GET /api/applications/gate) is `required` and no ?invite=
 *   token is present. Cosmetic only — the backend gate in
 *   routes/applications.js POST / is the enforcement. Never shown while the
 *   mode is `optional`/`disabled`.
 * - 'invalid': a ?invite= token IS present but the public status check says
 *   it is no longer valid (revoked / expired / already used / unknown).
 *   Shown regardless of the mode — a present token is always honored.
 *
 * #1017 (architecture-review addendum) adds two more, both reached only via a
 * ?code= or typed application code — never via ?invite=:
 * - 'cycle-closed': the code's check symbol is fine and it would otherwise
 *   redeem, but its cycle has sealed (closed or archived). Carries an
 *   optional `notice` prop — the staff-authored per-cycle copy — which
 *   overrides the default closed-round lede when non-empty.
 * - 'typo': the code's check symbol is wrong, so the code was mistyped. This
 *   is computed offline (no database access), so telling the family "typo"
 *   here reveals nothing an attacker couldn't already compute themselves.
 * Both distinctions stop at the code level — every OTHER kind of invalid
 * code (unknown / expired / revoked / exhausted) still collapses to
 * 'invalid', same as today.
 *
 * GH-415: the 'missing' variant also carries an inline application-code entry
 * form, so a code shared verbally or on a flyer (not as a link) still opens the
 * application. A valid code is stashed for createDraft and handed back to the
 * parent via onCodeAccepted, which swaps this screen for the signup form.
 *
 * GH-582 (ledger D-2, `docs/DECISIONS.md` 2026-08-10 "Intake is referral-only"):
 * BOTH variants used to end on "Or reach out to the Tyler Robinson Foundation
 * team directly" with no link, email or phone — a dead end for exactly the
 * family this screen exists to help, and one that already renders today on the
 * 'invalid' path even while application_codes_mode is `optional`. Every point
 * that tells a family to go somewhere now carries the place to go.
 *
 * GH-620: this screen used to carry a second destination,
 * trf.org/partnerhospitalsandcenters/, for families who don't know who their
 * hospital social worker is. That URL 404s (verified live 2026-08-12, browser
 * UA, following redirects: trf.org/partnerhospitalsandcenters/ -> 301 ->
 * www.trf.org/partnerhospitalsandcenters -> 301 -> www.trf.org/... -> 404;
 * trf.org/get-support still returns 200). Interim fix, both variants: drop the
 * dead link and fold its "not sure who to ask" guidance into the surviving
 * get-support point, so the family still has one live thing to do instead of a
 * broken remedy. Revert this the day TRF hands back a working partner-hospitals
 * URL — see the draft memo in the #620 PR description.
 *
 * #1039 / AP-26 (PR-C): every word on this screen now resolves through
 * `copy()`, so TRF can change it without a developer. The wording below is no
 * longer here — it is the SEED of a registered slot in `lib/copySlots.ts`, and
 * `copySlots.verify.test.ts` pins that this file no longer carries a copy of
 * it. What a family sees is `override ?? last_known_good ?? seed`, and the
 * seeds are these exact sentences, so with the store empty or the API down this
 * screen renders byte-identically to what it rendered before. The two #1017
 * variants joined the registry at the merge of the two lanes (2026-08-25): the
 * former `applyCopy.ts` said "#1009 becomes a move, not a rewrite", and this
 * is that move.
 *
 * GH-620 above is also why the get-support anchors are `linked` slots rather
 * than a shared URL constant: the destination travels with the sentence that
 * names it, so fixing a moved page is one edit per visible link and never a
 * live link beside a dead one.
 */

/**
 * #2109 R4-B17 (brief B8): a staff-written closed-cycle notice is English
 * only. A non-English family reads the translated `apply.cycle_closed.lede`
 * default instead; the override shows only for an English reader.
 */
function useStaffNoticeFor(notice: string | null | undefined): string | null {
  const { i18n } = useTranslation();
  // The language the family CHOSE, not the one whose catalogs happen to be loaded.
  return (i18n.language ?? 'en').startsWith('en') && notice?.trim() ? notice : null;
}

/**
 * #3452: a Pacific calendar day (YYYY-MM-DD) in the reader's language —
 * "November 1, 2026", "1 de noviembre de 2026". Read at noon UTC so no
 * timezone can move it to the day before. '' when there is no day.
 */
function calendarDayWords(day: string | undefined, language: string | undefined): string {
  if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return '';
  try {
    return new Intl.DateTimeFormat(language || 'en', {
      year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
    }).format(new Date(`${day}T12:00:00Z`));
  } catch {
    return day;
  }
}

/** External link, editorial styling — quiet underline, brand ink on hover. */
function StopLink({ href, children }: { href: string; children: ReactNode }) {
  const className = 'text-stevens-ink underline decoration-stevens-indigo/40 underline-offset-4 transition-colors hover:text-stevens-indigo hover:decoration-stevens-indigo';
  // 2026-11-03: an email link opens the mail app, not a new tab — and must
  // not announce one.
  if (href.startsWith('mailto:')) return <a href={href} className={className}>{children}</a>;
  return (
    <ExternalLink
      href={href}
      className="text-stevens-ink underline decoration-stevens-indigo/40 underline-offset-4 transition-colors hover:text-stevens-indigo hover:decoration-stevens-indigo"
    >
      {children}
    </ExternalLink>
  );
}

export type InviteStopVariant = 'missing' | 'invalid' | 'cycle-closed' | 'typo' | 'new-families-soon';

/**
 * Which slot fills which hole, per variant. This is the shape `VARIANT_COPY`
 * used to have, with slot KEYS where the sentences were — so the structure of
 * the screen stays declared in one readable place and the wording stops living
 * here at all.
 */
const VARIANT_SLOTS: Record<InviteStopVariant, {
  /** A row with no `v` slot shows the screen's date instead (#3452's "New families from"). */
  meta: Array<{ id: string; k: ProseSlotKey; v?: ProseSlotKey }>;
  headline: SpanSlotKey;
  lede: ProseSlotKey;
  points: Array<{ id: string; slot: SpanSlotKey }>;
}> = {
  missing: {
    meta: [
      {
        id: 'how-to-apply',
        k: 'apply.invite_required.meta_how_to_apply_key',
        v: 'apply.invite_required.meta_how_to_apply_value',
      },
      {
        id: 'who-can-refer',
        k: 'apply.invite_required.meta_who_can_refer_key',
        v: 'apply.invite_required.meta_who_can_refer_value',
      },
    ],
    headline: 'apply.invite_required.headline',
    lede: 'apply.invite_required.lede',
    points: [
      { id: 'social-worker', slot: 'apply.invite_required.point_social_worker' },
      { id: 'contact-trf', slot: 'apply.invite_required.point_contact_trf' },
      { id: 'ready', slot: 'apply.invite_required.point_ready' },
    ],
  },
  invalid: {
    meta: [
      {
        id: 'this-link',
        k: 'apply.invite_invalid.meta_this_link_key',
        v: 'apply.invite_invalid.meta_this_link_value',
      },
      {
        id: 'what-to-do',
        k: 'apply.invite_invalid.meta_what_to_do_key',
        v: 'apply.invite_invalid.meta_what_to_do_value',
      },
    ],
    headline: 'apply.invite_invalid.headline',
    lede: 'apply.invite_invalid.lede',
    points: [
      { id: 'social-worker', slot: 'apply.invite_invalid.point_social_worker' },
      { id: 'contact-trf', slot: 'apply.invite_invalid.point_contact_trf' },
      { id: 'nothing-lost', slot: 'apply.invite_invalid.point_nothing_lost' },
    ],
  },
  'cycle-closed': {
    meta: [
      {
        id: 'this-round',
        k: 'apply.cycle_closed.meta_this_round_key',
        v: 'apply.cycle_closed.meta_this_round_value',
      },
      {
        id: 'what-to-do',
        k: 'apply.cycle_closed.meta_what_to_do_key',
        v: 'apply.cycle_closed.meta_what_to_do_value',
      },
    ],
    headline: 'apply.cycle_closed.headline',
    lede: 'apply.cycle_closed.lede',
    points: [
      { id: 'social-worker', slot: 'apply.cycle_closed.point_social_worker' },
      { id: 'contact-trf', slot: 'apply.cycle_closed.point_contact_trf' },
    ],
  },
  // #3452: the round is open to renewing families only until its
  // "New families from" day.
  'new-families-soon': {
    meta: [
      {
        id: 'this-round',
        k: 'apply.new_families_soon.meta_this_round_key',
        v: 'apply.new_families_soon.meta_this_round_value',
      },
      { id: 'new-families-from', k: 'apply.new_families_soon.meta_opens_key' },
    ],
    headline: 'apply.new_families_soon.headline',
    lede: 'apply.new_families_soon.lede',
    points: [
      { id: 'contact-trf', slot: 'apply.new_families_soon.point_contact_trf' },
    ],
  },
  typo: {
    meta: [
      {
        id: 'that-code',
        k: 'apply.code_typo.meta_that_code_key',
        v: 'apply.code_typo.meta_that_code_value',
      },
      {
        id: 'what-to-do',
        k: 'apply.code_typo.meta_what_to_do_key',
        v: 'apply.code_typo.meta_what_to_do_value',
      },
    ],
    headline: 'apply.code_typo.headline',
    lede: 'apply.code_typo.lede',
    points: [
      { id: 'social-worker', slot: 'apply.code_typo.point_social_worker' },
      { id: 'contact-trf', slot: 'apply.code_typo.point_contact_trf' },
    ],
  },
};

/**
 * GH-415 — inline application-code entry, editorial styling (no shadcn Dialog).
 * Auto-uppercases as typed and accepts pasted hyphens/spaces verbatim; the
 * backend normalizes (strips whitespace/hyphens/`TRF`, maps O→0, I/L→1).
 *
 * #1017 (architecture-review addendum): typing is where typos happen — this
 * is the one surface the check symbol was built for. `res.data` now carries
 * two failure shapes and each gets its own message: `typo` (the check
 * symbol is wrong — computed, no database access, so naming it leaks
 * nothing), and everything else keeps today's single generic message. A code
 * carries no cycle since 2026-10-28, so a code never reads as closed. No other distinction is ever
 * surfaced — an unknown, expired, revoked, or exhausted code is
 * indistinguishable on purpose.
 *
 * Baymard checkout research attributes ~8% of abandonment to a visible code
 * field the visitor can't fill — a prominent empty box reads as "you're
 * missing something" to the majority of families who have no code at all.
 * So this lives behind a closed-by-default disclosure instead of an
 * always-open form.
 *
 * Exported (#1018 Task 15): `ApplyLandingPage` reuses this same disclosure
 * on the ordinary signup path in `optional` mode — the "Have a code?" entry
 * point is identical whether it's reached from the stop screen or from
 * alongside the signup form, so it is not reimplemented there. It reads the
 * copy store itself (the query is shared with any parent through react-query,
 * so this costs no second read) rather than taking `copy` as a prop, so both
 * call sites stay a one-liner.
 */
export function ApplicationCodeEntry({
  onCodeAccepted,
  initialCode,
  defaultExpanded = false,
  heading,
}: {
  /** `renewal` is true for a valid code with Renewal Families on (2026-10-28). */
  onCodeAccepted: (code: string, opts?: { renewal: boolean }) => void;
  /**
   * Wave launch-w1014 L3-a: the box's own heading, when the screen around it
   * names it differently ("Enter your code to continue" on the wizard's
   * refused-start stop). Defaults to the registered eyebrow slot.
   */
  heading?: string;
  /**
   * #2692: the code that brought the family here, so a mistyped link's code
   * can be corrected in place instead of retyped from nothing.
   */
  initialCode?: string;
  /** #2692: the typo stop IS the code box, so it opens already. */
  defaultExpanded?: boolean;
}) {
  const { copy } = useCopy();
  const { t: tf } = useTranslation('forms');
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [code, setCode] = useState((initialCode ?? '').toUpperCase());
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // #1152: in `optional` mode the parent renders nothing in response to
  // `onCodeAccepted`, so without this the accepted path is a spinner flicker
  // with the code still sitting in the box — indistinguishable from a no-op.
  const [accepted, setAccepted] = useState(false);

  const accept = (raw: string, renewal = false) => {
    stashApplyCode(raw);
    setError(null);
    setAccepted(true);
    onCodeAccepted(raw, { renewal });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const raw = code.trim();
    if (!raw || checking || accepted) return;
    setChecking(true);
    setError(null);
    try {
      const res = await api.applications.codeStatus(raw);
      if (res.data?.valid) {
        accept(raw, res.data.renewal === true);
        return;
      }
      if (res.data?.typo) {
        setError(copy('apply.code_typo.headline'));
      } else {
        setError(copy('apply.invite_required.code_entry_error_invalid'));
      }
    } catch {
      // Network failure is not the family's problem — fail open. The backend
      // re-validates on draft creation, so nothing slips through.
      accept(raw);
      return;
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="mb-10 max-w-[420px]">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls="application-code-panel"
        className="flex items-center gap-1.5 text-left"
      >
        <MonoLabel tone="indigo">{heading ?? copy('apply.invite_required.code_entry_eyebrow')}</MonoLabel>
        <span aria-hidden className="text-stevens-indigo text-[11px]">
          {expanded ? '▾' : '▸'}
        </span>
      </button>
      {expanded && (
        <form id="application-code-panel" onSubmit={handleSubmit} className="mt-4" noValidate>
          <p className="text-body leading-snug text-stevens-ink-2 mb-4">
            {copy('apply.invite_required.code_entry_helper')}
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <div className="flex-1">
              <FieldLabel htmlFor="application-code">{copy('apply.invite_required.code_entry_field_label')}</FieldLabel>
              <input
                id="application-code"
                name="application-code"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  if (error) setError(null);
                }}
                // 2026-10-11 (J1 c04): a bare realistic code read as a pre-filled
                // box in dark mode; it is now marked as an example, in the #2521
                // placeholder token and without the box's upper-casing.
                placeholder={tf('inviteStop.codePlaceholder', { example: 'TRF-FALL27-CF1XA' })}
                autoComplete="off"
                // Wave launch-w1014 L3-b: a phone keyboard must not "fix" a code.
                autoCorrect="off"
                autoCapitalize="characters"
                spellCheck={false}
                disabled={accepted}
                aria-invalid={error ? true : undefined}
                aria-describedby={
                  error ? 'application-code-error' : accepted ? 'application-code-accepted' : undefined
                }
                className={`mt-2 w-full rounded-sharp border bg-stevens-surface px-4 py-3 font-mono text-lede uppercase text-stevens-ink placeholder:text-stevens-ink-placeholder placeholder:normal-case placeholder:tracking-normal transition-colors focus:outline-none focus:border-stevens-indigo ${
                  error ? 'border-status-danger-border' : 'border-stevens-indigo/25'
                }`}
              />
            </div>
            <button
              type="submit"
              disabled={checking || accepted || !code.trim()}
              className="flex items-center justify-center gap-2 rounded-sharp bg-stevens-indigo-solid px-6 py-3 text-body font-medium text-stevens-indigo-solid-fg transition-colors hover:bg-stevens-indigo-solid-2 disabled:opacity-60 sm:mt-[26px]"
            >
              {checking && <Loader2 className="h-4 w-4 animate-spin" />}
              {copy('apply.invite_required.code_entry_submit')}
            </button>
          </div>
          {error && (
            <p
              id="application-code-error"
              role="alert"
              className="mt-3 text-body-sm leading-snug text-status-danger-fg"
            >
              {error}
            </p>
          )}
          {accepted && (
            <p
              id="application-code-accepted"
              role="status"
              className="mt-3 text-body-sm leading-snug text-status-ok-fg"
            >
              {copy('apply.invite_required.code_entry_accepted')}
            </p>
          )}
        </form>
      )}
    </div>
  );
}

export function InviteRequiredStop({
  variant = 'missing',
  onCodeAccepted,
  notice,
  onReferralChange,
  initialCode,
  onContinueWithoutCode,
  newFamiliesOpenOn,
}: {
  variant?: InviteStopVariant;
  /** #3452: for `variant="new-families-soon"` — the Pacific day (YYYY-MM-DD) new families may apply from. */
  newFamiliesOpenOn?: string;
  onCodeAccepted?: (code: string, opts?: { renewal: boolean }) => void;
  /**
   * #2692: for `variant="typo"` — the mistyped code from the link, offered
   * back in the code box so it can be fixed.
   */
  initialCode?: string;
  /**
   * #2692: for `variant="typo"` — the way on that the screen's own copy
   * promises ("carry on without it"). The parent drops the bad code.
   */
  onContinueWithoutCode?: () => void;
  /**
   * #1017: the staff-authored per-cycle closed notice, passed only for
   * `variant="cycle-closed"`. Overrides the default closed-round lede when
   * non-empty; an empty/whitespace notice (staff never wrote one) falls back
   * to the registered slot instead of rendering blank.
   */
  notice?: string;
  /**
   * #1018: observe the referral "slow path" block's value as the family
   * fills it in. Optional — a caller that doesn't pass one still gets a
   * working, controlled `ReferralBlock` (this component holds its own
   * local state), it just isn't threaded anywhere. `ApplyLandingPage` passes
   * `stashApplyReferral` here so the value survives the signup round-trip
   * the same way `stashApplyCode` does; `applications.createDraft` reads the
   * stash back and sends it as `referral_hospital_id` /
   * `referral_hospital_other` / `referral_contact_name` (#1018 Task 14).
   */
  onReferralChange?: (value: ReferralValue) => void;
}) {
  const [, setReferralValue] = useState<ReferralValue>({
    hospital_id: null,
    hospital_other: '',
    contact_name: '',
  });
  const { copy, ready } = useCopy();
  const { t, i18n } = useTranslation('forms');
  const staffNotice = useStaffNoticeFor(notice);
  const opensOnWords = calendarDayWords(newFamiliesOpenOn, i18n.language);
  const slots = VARIANT_SLOTS[variant];

  // Guardrail 7. Hold the paint until the copy read has SETTLED — succeeded or
  // failed, either is an answer and neither takes more than one attempt.
  // Painting the seed and swapping in the override a moment later would show a
  // family one wording replaced by another mid-read, on the screen that is
  // telling them whether they can apply at all.
  // #2702: latch it. `ready` falls back to false whenever the copy query goes
  // back to pending, and a child that reads the same query (ApplicationCodeEntry)
  // refetches an errored query on mount — so without the latch a failed read
  // unmounted the child, refetched, failed and mounted it again, thousands of
  // requests in seconds. Once settled, this screen stays painted.
  const readyOnce = useRef(false);
  if (ready) readyOnce.current = true;
  if (!readyOnce.current) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        role="status"
        aria-label={t('inviteStop.loading')}
      >
        <Loader2 className="h-6 w-6 animate-spin text-stevens-indigo" />
      </div>
    );
  }

  // #1017: staff wording for THIS cycle beats the registry's default. The
  // registry default is still what TRF edits for every cycle that has none.
  // #2109 R4-B17: only for an English reader (useStaffNoticeFor).
  const lede = variant === 'cycle-closed' && staffNotice ? staffNotice : copy(slots.lede);

  return (
    <AuthShell
      documentLabel={copy('apply.invite_stop.document_label')}
      meta={slots.meta.map((row) => ({ k: copy(row.k), v: row.v ? copy(row.v) : opensOnWords }))}
      alternate={{
        prompt: copy('apply.invite_stop.alternate_prompt'),
        to: '/login',
        label: copy('apply.invite_stop.alternate_link_label'),
      }}
    >
      {/* Eyebrow */}
      <div className="mb-5">
        <MonoLabel>{copy('apply.invite_stop.eyebrow')}</MonoLabel>
      </div>

      {/* Headline */}
      <h1
        className="font-serif text-[clamp(28px,3.6vw,40px)] leading-[1.15] tracking-tight text-stevens-ink mb-4"
      >
        <CopySpan value={copy(slots.headline)} />
      </h1>

      {/* Lede */}
      <p className="text-lede leading-relaxed text-stevens-ink-2 mb-8 max-w-[420px]">
        {lede}
      </p>

      {/* What to do next */}
      <div className="mb-10 border-l-2 border-stevens-indigo/20 pl-5 flex flex-col gap-3">
        {slots.points.map((point) => (
          <p key={point.id} className="text-body text-stevens-ink-2 leading-snug">
            <CopySpan value={copy(point.slot)} link={StopLink} />
          </p>
        ))}
      </div>

      {/* GH-415: typed application code — a shared code works without a link.
          Wave launch-w1014 L3-b (D2): OPEN by default here. This variant only
          renders in `required` mode, where every family who can apply holds a
          code, so the box is the way on, not an accelerator. The Baymard
          closed-by-default rationale above still holds for `optional` mode,
          where ApplyLandingPage renders this entry collapsed. */}
      {variant === 'missing' && onCodeAccepted && (
        <ApplicationCodeEntry onCodeAccepted={onCodeAccepted} defaultExpanded />
      )}

      {/* #3452: a renewing family who reached /apply without its link types its code here. */}
      {variant === 'new-families-soon' && onCodeAccepted && (
        <ApplicationCodeEntry onCodeAccepted={onCodeAccepted} defaultExpanded />
      )}

      {/* #2692: a mistyped code is fixable here, and the screen's own lede
          says the family can carry on without it, so both are offered. */}
      {variant === 'typo' && onCodeAccepted && (
        <ApplicationCodeEntry onCodeAccepted={onCodeAccepted} initialCode={initialCode} defaultExpanded />
      )}
      {variant === 'typo' && onContinueWithoutCode && (
        <button
          type="button"
          onClick={onContinueWithoutCode}
          className="mb-10 text-body text-stevens-ink underline decoration-stevens-indigo/40 underline-offset-4 transition-colors hover:text-stevens-indigo hover:decoration-stevens-indigo"
        >
          {t('inviteStop.continueWithoutCode')}
        </button>
      )}

      {/* #1018: the referral "slow path" — open by default, unlike the code
          entry above it. A family with no code is the ordinary case this
          screen exists for, not the exception, so this block is not hidden
          behind a disclosure. Local state only for now; onReferralChange
          lets a parent (ApplyLandingPage) observe it once submission is
          wired to carry it through. */}
      {variant === 'missing' && (
        <ReferralBlock onChange={onReferralChange ?? setReferralValue} />
      )}

      {/* Footer attribution */}
      <p className="mt-10 text-caption text-stevens-ink-2/60">
        {copy('apply.invite_stop.footer_attribution')}
      </p>
    </AuthShell>
  );
}
